/**
 * What a finding card shows besides the engine's words: the count path that leads to the row
 * ("112 → 98 → 84"), and what the stated/computed pair becomes once a proposed fix is applied.
 */
import type { DiagnosticGroup } from '../diagnostics/group';
import { fixKindCode } from '../engine/localize';
import type { CompileResult, Diagnostic, Fix } from '../engine/types';

/**
 * Stitch counts for `size` from the first row up to line `lineNo` (included): the count before
 * the first row, then every count carried to the next row, consecutive repeats collapsed.
 */
export function countPath(result: CompileResult, lineNo: number, size: number): number[] {
  const path: number[] = [];
  const rows = [...result.rows].filter((r) => r.line_no <= lineNo).sort((a, b) => a.line_no - b.line_no);
  for (const row of rows) {
    const ps = row.per_size.find((p) => p.size === size);
    if (!ps || !ps.applies) continue;
    if (path.length === 0 && ps.available !== null && ps.available !== undefined) path.push(ps.available);
    const after = ps.count_after;
    if (after !== null && after !== undefined && after !== path[path.length - 1]) path.push(after);
  }
  return path;
}

export interface CalcLine {
  /** Size index, or null when the same computation holds for every size. */
  size: number | null;
  /** The shown computation: the engine's own (`calc`) or the count path. */
  calc: string | null;
  expected: number | null;
  actual: number | null;
}

const hasNumber = (v: number | null | undefined): v is number => v !== null && v !== undefined;

/**
 * One computation line per size concerned by the group. Prefers the engine's `calc` (1.1.0),
 * otherwise rebuilds the path from rows[].per_size[].count_after.
 */
export function calcLines(group: DiagnosticGroup, result: CompileResult): CalcLine[] {
  const out: CalcLine[] = [];
  const seen = new Set<string>();
  const sizes = result.document.sizes.map((s) => s.index);
  for (const d of group.diagnostics) {
    const targets = d.size === null ? (sizes.length === 1 ? [sizes[0]] : [null]) : [d.size];
    for (const size of targets) {
      let calc = (d as Partial<Diagnostic>).calc ?? null;
      if (!calc && size !== null && group.line_no > 0) {
        const path = countPath(result, group.line_no, size);
        if (path.length >= 2) calc = path.join(' → ');
      }
      const expected = hasNumber(d.expected) ? d.expected : null;
      const actual = hasNumber(d.actual) ? d.actual : null;
      if (!calc && expected === null && actual === null) continue;
      const key = `${size}|${calc}|${expected}|${actual}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ size, calc, expected, actual });
    }
  }
  return out;
}

/**
 * What the stated/computed pair becomes with `fix`, for a count mismatch: a stated-count fix makes
 * the pattern state what the rows give; any other fix is re-verified by the engine to make the rows
 * give what the pattern states. Null when the fix does not settle a count.
 */
export function countAfterFix(line: CalcLine, fix: Fix): number | null {
  if (line.expected === null || line.actual === null) return null;
  if (line.size !== null && !fix.sizes.includes(line.size)) return null;
  return fixKindCode(fix) === 'stated_count' ? line.actual : line.expected;
}
